import { Eye, Search, Plus, TrendingDown, CheckCircle2, AlertTriangle, Package } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { WatchlistStats } from '../types';

interface WatchlistHeroProps {
  stats: WatchlistStats;
  filter: string;
  onFilterChange: (value: string) => void;
  onAddClick: () => void;
  used: number;
  limit: number;
}

function StatCard({ value, label, icon: Icon }: { value: string | number; label: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-xl font-bold text-white">{value}</div>
        <div className="text-xs text-primary-100">{label}</div>
      </div>
    </div>
  );
}

export function WatchlistHero({
  stats,
  filter,
  onFilterChange,
  onAddClick,
  used,
  limit,
}: WatchlistHeroProps) {
  const { t } = useLanguage();
  const isAtLimit = used >= limit && limit !== Infinity;

  return (
    <section className="relative min-h-[35vh] overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
          <Eye className="w-4 h-4" />
          {t('watchlist.badge', 'Price Tracking')}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
          {t('watchlist.heroTitle', 'Track your')}{' '}
          <span className="text-accent">{t('watchlist.heroTitleHighlight', 'favorites')}</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-4 text-lg text-primary-100 max-w-2xl animate-slide-up animation-delay-100">
          {t('watchlist.heroSubtitle', 'Monitor prices, get notified about drops, and track availability across stores.')}
        </p>

        {/* Search bar and Add button */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 max-w-2xl animate-slide-up animation-delay-200">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              value={filter}
              onChange={e => onFilterChange(e.target.value)}
              placeholder={t('watchlist.searchPlaceholder', 'Search by name or product code...')}
              className="w-full pl-14 pr-6 py-4 text-lg rounded-2xl bg-white/95 backdrop-blur-sm shadow-xl focus:ring-4 focus:ring-accent/30 focus:outline-none border-0 text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            onClick={onAddClick}
            disabled={isAtLimit}
            className={`flex items-center justify-center gap-2 px-6 py-4 text-base font-medium rounded-2xl shadow-xl transition-colors ${
              isAtLimit
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-white text-primary-700 hover:bg-gray-50'
            }`}
          >
            <Plus className="w-5 h-5" />
            {t('watchlist.addProduct', 'Add Product')}
          </button>
        </div>

        {/* Limit indicator */}
        {limit !== Infinity && (
          <div className="mt-4 text-sm text-primary-200 animate-fade-in animation-delay-200">
            {used} / {limit} {t('watchlist.itemsUsed', 'items used')}
          </div>
        )}

        {/* Stats row */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl animate-fade-in animation-delay-300">
          <StatCard
            value={stats.totalTracking}
            label={t('watchlist.stats.tracking', 'Tracking')}
            icon={Package}
          />
          <StatCard
            value={stats.priceDrops}
            label={t('watchlist.stats.priceDrops', 'Price Drops')}
            icon={TrendingDown}
          />
          <StatCard
            value={stats.inStock}
            label={t('watchlist.stats.inStock', 'In Stock')}
            icon={CheckCircle2}
          />
          <StatCard
            value={stats.lowStock}
            label={t('watchlist.stats.lowStock', 'Low Stock')}
            icon={AlertTriangle}
          />
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}
